/**
 * OrganisationFormDialog — one form for every organisation type. The type you
 * pick decides which parents are legal and which fields are shown.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, AlertTriangle } from 'lucide-react';

const FIELD_LABELS = {
  country: 'Country',
  region: 'Region / State',
  district: 'District',
  city: 'City',
  address: 'Address',
  currency: 'Base currency',
  website_url: 'Website URL',
  contact_name: 'Contact name',
  contact_email: 'Contact email',
  contact_phone: 'Contact phone',
  leader_name: 'Leader',
  overseer_name: 'Overseer',
  meeting_day: 'Meeting day',
  service_times: 'Service times',
  registration_number: 'Registration number',
  hq_address: 'HQ address',
  countries: 'Countries served',
};

const TYPE_HINTS = {
  global_ministry: 'The root of the whole ministry — it has no parent.',
  country: 'A national office. Its currency becomes the default for assemblies beneath it.',
  province: 'A province groups districts and assemblies inside one country (or several).',
  district: 'Optional layer between a Province and its Assemblies — a province with no districts works exactly as before.',
  assembly: 'A local church. Pick its District as the parent if the Province has districts, otherwise pick the Province.',
};

const emptyForm = {
  name: '', code: '', organisation_type: '', parent_id: '', status: 'active', description: '',
  countries: [], leader_member_id: '', leader_name: '', leader_title: '', leader_photo_url: '',
};

export const OrganisationFormDialog = ({ open, onOpenChange, editing, defaultParent }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [countryDraft, setCountryDraft] = useState('');
  const [uploading, setUploading] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ['org-types'],
    queryFn: async () => (await api.get('/admin/organisations/meta/types')).data,
    enabled: open,
  });

  const { data: leaderOptions = [] } = useQuery({
    queryKey: ['org-leader-options', editing?.id],
    queryFn: async () => (await api.get(`/admin/organisations/${editing.id}/leader-options`)).data,
    enabled: open && !!editing?.id,
  });

  const { data: flat } = useQuery({
    queryKey: ['org-flat'],
    queryFn: async () => (await api.get('/admin/organisations')).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyForm,
        ...Object.fromEntries(Object.entries(editing).filter(([, v]) => v !== null)),
        parent_id: editing.parent_id || '',
        countries: editing.countries || [],
        owns_property: editing.owns_property ?? editing.organisation_type === 'campus',
      });
    } else if (defaultParent) {
      const firstChildType = (meta?.types || [])
        .find((t) => t.parents.includes(defaultParent.organisation_type));
      setForm({
        ...emptyForm,
        parent_id: defaultParent.id,
        organisation_type: firstChildType?.key || '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, editing, defaultParent, meta]);

  const types = meta?.types || [];
  const selectedType = types.find((t) => t.key === form.organisation_type);
  const hqId = (flat || []).find((o) => o.organisation_type === 'global_ministry')?.id || '';
  const parentOptions = useMemo(() => {
    if (!selectedType || !flat) return [];
    return flat.filter((o) => selectedType.parents.includes(o.organisation_type)
                              && o.id !== editing?.id);
  }, [selectedType, flat, editing]);

  // control_mode and owns_property drive the hierarchy, so always send them.
  const extraPayload = () => {
    const extra = {};
    if (form.organisation_type === 'country') extra.control_mode = form.control_mode || 'independent';
    if (['assembly', 'campus'].includes(form.organisation_type)) {
      extra.owns_property = !!form.owns_property;
    }
    return extra;
  };

  const save = useMutation({
    mutationFn: async (payload) => editing
      ? (await api.put(`/admin/organisations/${editing.id}`, payload)).data
      : (await api.post('/admin/organisations', payload)).data,
    onSuccess: (res) => {
      toast.success(`${res.name} ${editing ? 'updated' : 'created'}`);
      queryClient.invalidateQueries({ queryKey: ['org-tree'] });
      queryClient.invalidateQueries({ queryKey: ['org-flat'] });
      queryClient.invalidateQueries({ queryKey: ['admin-churches'] });
      queryClient.invalidateQueries({ queryKey: ['org-leaders'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.organisation_type) return toast.error('Pick an organisation type');
    const allowed = new Set([...(selectedType?.fields || []), 'description']);
    const payload = {
      name: form.name.trim(),
      organisation_type: form.organisation_type,
      status: form.status,
      parent_id: selectedType?.is_root ? null : (form.parent_id || null),
    };
    if (form.code?.trim()) payload.code = form.code.trim();
    allowed.forEach((f) => { if (form[f]) payload[f] = form[f]; });
    Object.assign(payload, extraPayload());
    // Clearable fields are always sent — an empty string means "remove this",
    // whereas omitting the key would silently keep the old value.
    if (editing || (form.countries || []).length) payload.countries = form.countries || [];
    if (editing || form.is_church_plant) payload.is_church_plant = !!form.is_church_plant;
    ['leader_member_id', 'leader_name', 'leader_title', 'leader_photo_url', 'description']
      .forEach((f) => {
        if (editing || form[f]) payload[f] = form[f] || '';
      });
    save.mutate(payload);
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="organisation-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add Organisation'}</DialogTitle>
          <DialogDescription>
            The type you pick decides which parents are legal and which fields appear.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="org-type">Organisation type *</Label>
              <select
                id="org-type"
                data-testid="org-form-type"
                value={form.organisation_type}
                onChange={(e) => setForm((p) => ({ ...p, organisation_type: e.target.value, parent_id: '' }))}
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                <option value="">— pick a type —</option>
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-parent">Parent {selectedType?.is_root ? '' : '*'}</Label>
              <select
                id="org-parent"
                data-testid="org-form-parent"
                disabled={!selectedType || selectedType.is_root}
                value={form.parent_id}
                onChange={set('parent_id')}
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              >
                <option value="">
                  {selectedType?.is_root ? '— top of the tree —' : '— pick a parent —'}
                </option>
                {parentOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.name} · {o.type_label}</option>
                ))}
              </select>
              {selectedType && !selectedType.is_root && (
                <div className="text-xs text-slate-500">
                  Allowed parents:{' '}
                  {selectedType.parents.map((p) => (
                    <Badge key={p} variant="outline" className="mr-1 text-[10px]">
                      {types.find((t) => t.key === p)?.label || p}
                    </Badge>
                  ))}
                </div>
              )}
              {selectedType && !selectedType.is_root && parentOptions.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  No eligible parent exists yet — create one first.
                </p>
              )}
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="org-name">Name *</Label>
              <Input id="org-name" data-testid="org-form-name" value={form.name}
                     onChange={set('name')} placeholder="e.g. Harare Province" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-code">Code</Label>
              <Input id="org-code" data-testid="org-form-code" value={form.code || ''}
                     onChange={set('code')} placeholder="auto-generated when blank" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="org-status">Status</Label>
              <select id="org-status" data-testid="org-form-status" value={form.status}
                      onChange={set('status')}
                      className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                {(meta?.statuses || ['active', 'inactive', 'archived']).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {form.organisation_type === 'assembly' && (
              <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700"
                     data-testid="org-form-plant-toggle">
                <input type="checkbox" checked={!!form.is_church_plant}
                       onChange={(e) => setForm((p) => ({ ...p, is_church_plant: e.target.checked }))} />
                Still a church plant
                <span className="text-xs text-slate-500">— untick once it graduates into a full assembly</span>
              </label>
            )}
          </div>

          {selectedType && TYPE_HINTS[selectedType.key] && (
            <p className="text-xs text-slate-500" data-testid="org-form-type-hint">
              {TYPE_HINTS[selectedType.key]}
            </p>
          )}

          {selectedType && !['country', 'district'].includes(selectedType.key) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4"
                 data-testid="org-form-type-fields">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {selectedType.label} details
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedType.fields.filter((f) => f !== 'countries').map((f) => (
                  <div key={f} className="space-y-1.5">
                    <Label htmlFor={`org-f-${f}`} className="text-xs">
                      {FIELD_LABELS[f] || f}
                    </Label>
                    <Input id={`org-f-${f}`} data-testid={`org-form-field-${f}`}
                           value={form[f] || ''} onChange={set(f)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {form.organisation_type === 'country' && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-3"
                 data-testid="org-form-country-block">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                  Country office details
                </p>
                <p className="mt-0.5 text-xs text-emerald-700/80">
                  Everything beneath this office inherits its base currency by default.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">How is this office run?</Label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[['provincial', 'Under Provincial Control', 'Attached to a Province and managed by that Province\'s overseer.'],
                    ['independent', 'Independent', 'Reports straight to the Global Ministry (HQ).']].map(([key, label, hint]) => (
                    <button key={key} type="button"
                            onClick={() => setForm((f) => ({ ...f, control_mode: key,
                              parent_id: key === 'independent' ? (hqId || f.parent_id) : f.parent_id }))}
                            className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                              (form.control_mode || 'independent') === key
                                ? 'border-emerald-400 bg-white' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid={`org-form-control-mode-${key}`}>
                      <span className="block text-xs font-semibold text-slate-800">{label}</span>
                      <span className="block text-[10px] text-slate-500">{hint}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-emerald-700/80">
                  A Super Admin can promote a provincially controlled office to Independent later.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="org-country-name" className="text-xs">Country name *</Label>
                  <Input id="org-country-name" data-testid="org-form-country-name"
                         value={form.country || ''} onChange={set('country')}
                         placeholder="Zimbabwe" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-country-currency" className="text-xs">Base currency</Label>
                  <Input id="org-country-currency" data-testid="org-form-country-currency"
                         value={form.currency || ''} onChange={set('currency')} placeholder="USD" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-country-website" className="text-xs">Website</Label>
                  <Input id="org-country-website" data-testid="org-form-country-website"
                         value={form.website_url || ''} onChange={set('website_url')}
                         placeholder="https://…" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-country-overseer" className="text-xs">Regional overseer</Label>
                  <Input id="org-country-overseer" data-testid="org-form-country-overseer"
                         value={form.overseer_name || ''} onChange={set('overseer_name')}
                         placeholder="Rev. …" />
                </div>
              </div>
            </div>
          )}

          {form.organisation_type === 'district' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 space-y-3"
                 data-testid="org-form-district-block">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  District details
                </p>
                <p className="mt-0.5 text-xs text-amber-700/80">
                  A District belongs to one Province and can hold many Assemblies. It is optional —
                  provinces without districts keep working exactly as before.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="org-district-region" className="text-xs">Region</Label>
                  <Input id="org-district-region" data-testid="org-form-district-region"
                         value={form.region || ''} onChange={set('region')}
                         placeholder="Manicaland East" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="org-district-country" className="text-xs">Country</Label>
                  <Input id="org-district-country" data-testid="org-form-district-country"
                         value={form.country || ''} onChange={set('country')} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="org-district-overseer" className="text-xs">Overseer name</Label>
                  <Input id="org-district-overseer" data-testid="org-form-district-overseer"
                         value={form.overseer_name || ''} onChange={set('overseer_name')} />
                </div>
              </div>
            </div>
          )}

          {['assembly', 'campus'].includes(form.organisation_type) && (
            <label className="flex items-start gap-2 rounded-lg border border-cyan-200 bg-cyan-50/60 p-3"
                   data-testid="org-form-owns-property">
              <input type="checkbox" className="mt-0.5"
                     checked={!!form.owns_property}
                     onChange={(e) => setForm((f) => ({
                       ...f,
                       owns_property: e.target.checked,
                       // Ticking promotes an Assembly to a Campus; unticking sends it back.
                       organisation_type: e.target.checked ? 'campus' : 'assembly',
                     }))}
                     data-testid="org-form-owns-property-checkbox" />
              <span>
                <span className="block text-xs font-semibold text-cyan-900">This assembly owns property</span>
                <span className="block text-[10px] text-cyan-800/80">
                  It becomes a <strong>Campus</strong> — every Assembly feature stays, plus a Properties
                  tab for buildings and facilities. Turning this off again needs its buildings removed first.
                </span>
              </span>
            </label>
          )}

          {form.organisation_type === 'province' && (
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-4 space-y-3"
                 data-testid="org-form-countries">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">
                  Countries served
                </p>
                <p className="text-xs text-sky-700/80 mt-0.5">
                  A province serving more than one country sits directly under the Global Ministry.
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(form.countries || []).map((c) => (
                  <Badge key={c} variant="outline" className="bg-white gap-1" data-testid={`org-country-chip-${c}`}>
                    {c}
                    <button type="button" className="text-slate-400 hover:text-rose-600"
                            onClick={() => setForm((p) => ({ ...p, countries: p.countries.filter((x) => x !== c) }))}>
                      ×
                    </button>
                  </Badge>
                ))}
                {(form.countries || []).length === 0 && (
                  <span className="text-xs text-slate-500">No countries added yet</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={countryDraft}
                  onChange={(e) => setCountryDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = countryDraft.trim();
                      if (v && !(form.countries || []).includes(v)) {
                        setForm((p) => ({ ...p, countries: [...(p.countries || []), v] }));
                      }
                      setCountryDraft('');
                    }
                  }}
                  placeholder="Add a country and press Enter"
                  data-testid="org-country-input"
                />
                <Button type="button" variant="outline" data-testid="org-country-add"
                        onClick={() => {
                          const v = countryDraft.trim();
                          if (v && !(form.countries || []).includes(v)) {
                            setForm((p) => ({ ...p, countries: [...(p.countries || []), v] }));
                          }
                          setCountryDraft('');
                        }}>
                  Add
                </Button>
              </div>
              {(form.countries || []).length > 1 && (
                <p className="text-xs text-sky-800">
                  Multi-country province — set the parent to the Global Ministry.
                </p>
              )}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-4 space-y-4" data-testid="org-form-leader">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Leadership</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="org-leader-title">Title</Label>
                <Input id="org-leader-title" data-testid="org-form-leader-title"
                       value={form.leader_title || ''} onChange={set('leader_title')}
                       placeholder={selectedType ? `e.g. ${selectedType.label} leader` : 'Title'} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-leader-member">Pick a member</Label>
                <select
                  id="org-leader-member"
                  data-testid="org-form-leader-member"
                  value={form.leader_member_id || ''}
                  onChange={(e) => {
                    const picked = leaderOptions.find((m) => m.id === e.target.value);
                    setForm((p) => ({
                      ...p,
                      leader_member_id: e.target.value,
                      leader_name: picked?.name || p.leader_name,
                      leader_photo_url: picked?.photo_url || p.leader_photo_url,
                    }));
                  }}
                  disabled={!editing}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                >
                  <option value="">
                    {editing ? '— not from the member list —' : 'save first, then pick a member'}
                  </option>
                  {leaderOptions.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}{m.role ? ` · ${m.role}` : ''}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="org-leader-name">Or type a name</Label>
                <Input id="org-leader-name" data-testid="org-form-leader-name"
                       value={form.leader_name || ''} onChange={set('leader_name')}
                       placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Photo</Label>
                <div className="flex items-center gap-3">
                  {form.leader_photo_url ? (
                    <img src={form.leader_photo_url} alt="Leader"
                         data-testid="org-form-leader-preview"
                         className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200" />
                  ) : (
                    <span className="h-10 w-10 rounded-full bg-slate-100 grid place-items-center text-slate-400 text-xs">
                      —
                    </span>
                  )}
                  <input
                    type="file" accept="image/*" data-testid="org-form-leader-photo"
                    disabled={!editing || uploading}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f || !editing) return;
                      const fd = new FormData();
                      fd.append('file', f);
                      setUploading(true);
                      try {
                        const r = await api.post(`/admin/organisations/${editing.id}/leader-photo`, fd);
                        setForm((p) => ({ ...p, leader_photo_url: r.data.leader_photo_url }));
                        queryClient.invalidateQueries({ queryKey: ['org-tree'] });
                        queryClient.invalidateQueries({ queryKey: ['org-leaders'] });
                        toast.success('Photo uploaded');
                      } catch (err) {
                        toast.error(getErrorMessage(err));
                      } finally {
                        setUploading(false);
                      }
                    }}
                    className="text-xs"
                  />
                </div>
                {!editing && (
                  <p className="text-xs text-slate-500">Create the organisation first, then add a photo.</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org-desc">Description</Label>
            <Textarea id="org-desc" data-testid="org-form-description" rows={3}
                      value={form.description || ''} onChange={set('description')} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="org-form-cancel">Cancel</Button>
            <Button type="submit" disabled={save.isPending} data-testid="org-form-submit">
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Save changes' : 'Create organisation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrganisationFormDialog;
